// Phase 2 — Partner product manifest for the "Helpful tools" section.
//
// Each product has bilingual ZH/EN content, optional discount codes, an
// external URL (opened in a new tab with rel=noopener noreferrer), a safety
// note, and an `eligibility(submission)` predicate that decides whether the
// product should appear on a given mother's combined result page.
//
// Eligibility runs AFTER the result page has resolved its primary + related
// concerns, the user's confirmed goal, and the overall risk level. The aim is
// to NEVER show a product that contradicts the medical guidance shown above
// it (e.g. milk booster during mastitis Red, or milk booster when the goal is
// to wean).

import { getAllConcernIds } from './combinedConcerns';

// Helper to read normalised submission fields safely.
const ctx = (submission) => {
  const primary = submission?.primary_concern || submission?.main_concern || null;
  const related = Array.isArray(submission?.related_concerns) ? submission.related_concerns : [];
  const allIds = getAllConcernIds(submission || {});
  const goal = submission?.goal_confirmation || null;
  const risk = submission?.risk_level || 'green';
  const fu = submission?.concern_followups || {};
  const ppa = submission?.postpartum_age || '';
  return { primary, related, allIds, goal, risk, fu, ppa };
};

export const PRODUCTS = [
  {
    id: 'superlact_plus',
    name_zh: 'SuperLact Plus 催奶补充品',
    name_en: 'SuperLact Plus Milk Booster',
    image_url: '',
    url: 'https://supermamalab.com.my/products/superlact-plus-milk-booster',
    cta_label_zh: '查看产品',
    cta_label_en: 'View product',
    tagline_zh: '为有意提升或维持奶量的妈妈而设',
    tagline_en: 'Designed to support mothers aiming to increase or maintain supply',
    discounts: [
      { code: 'YOU&ME20', desc_zh: '满 RM150 可用', desc_en: 'Min spend RM150' },
      { code: 'YOU&ME40', desc_zh: '首次购买且满 RM250', desc_en: 'First-time buyer, spend RM250+' },
    ],
    safety_note_zh: '催奶补充品是辅助，不能保证一定增加奶量。它不能取代检查含乳、挤奶器配适、乳汁排出和宝宝摄入量。',
    safety_note_en: 'Milk boosters are optional support and do not guarantee a supply increase. They should not replace checking latch, pump fit, milk removal, and baby intake.',
    eligibility: (submission) => {
      const { allIds, goal, risk } = ctx(submission);
      // Only show when low_supply / work_pump concerns are present
      const relevant = allIds.includes('low_supply') || allIds.includes('work_pump');
      if (!relevant) return false;
      // Never show during mastitis Red or true red-flag risk
      if (risk === 'red') return false;
      // Never show during active mastitis concern (even if Yellow)
      if (allIds.includes('mastitis')) return false;
      // Never show during clog (active blockage) — keep flow moving first
      if (allIds.includes('clog')) return false;
      // Never show when weaning is the goal OR weaning is in the concerns
      if (goal === 'wean') return false;
      if (allIds.includes('wean')) return false;
      // Show only when the goal is to increase / maintain. If goal_confirmation
      // is not set (no conflict), default to allow (mother chose low_supply
      // without a competing wean signal).
      if (goal && goal !== 'increase' && goal !== 'maintain') return false;
      return true;
    },
  },

  {
    id: 'lanolin_balm',
    name_zh: 'Lanolin 乳头润唇修复膏',
    name_en: 'Lanolin Nipple & Lip Balm',
    image_url: '',
    url: 'https://supermamalab.com.my/products/lanolin-nipple-lip-balm',
    cta_label_zh: '查看产品',
    cta_label_en: 'View product',
    tagline_zh: '乳头干燥、破皮或不适时的表层舒缓',
    tagline_en: 'Surface comfort for dry, cracked, or sore nipples',
    discounts: [
      { code: 'YOU&ME20', desc_zh: '满 RM150 可用', desc_en: 'Min spend RM150' },
      { code: 'YOU&ME40', desc_zh: '首次购买且满 RM250', desc_en: 'First-time buyer, spend RM250+' },
    ],
    safety_note_zh: '可帮助表层舒缓，但持续破皮、流血、感染迹象或含乳痛仍需要专业评估。如果疼痛是含乳方式造成的，乳头膏不能取代调整含乳。',
    safety_note_en: 'May ease surface comfort, but persistent cracks, bleeding, signs of infection, or latch-driven pain need professional assessment. If pain comes from a shallow latch, balm does not replace fixing the latch.',
    eligibility: (submission) => {
      const { allIds, risk } = ctx(submission);
      // Show for nipple_pain
      if (!allIds.includes('nipple_pain')) return false;
      // Never show for true red flags
      if (risk === 'red') return false;
      return true;
    },
    // Optional contextual note: when nipple_pain + latch, balm is comfort-only
    extra_note: (submission, lang) => {
      const { allIds } = ctx(submission);
      if (allIds.includes('nipple_pain') && allIds.includes('latch')) {
        return lang === 'zh'
          ? '注意：如果你的乳头痛主要是含乳造成，乳头膏只是辅助表层舒缓，调整含乳才是关键。'
          : 'Note: If your nipple pain is primarily latch-driven, this balm only provides surface comfort — fixing the latch is the real solution.';
      }
      return null;
    },
  },

  {
    id: 'haakaa_gen2',
    name_zh: 'Haakaa 第二代硅胶集奶器（含吸盘）',
    name_en: 'Haakaa Gen 2 Silicone Breast Pump (with Suction Base)',
    image_url: '',
    url: 'https://haakaa.com.my/products/generation-2-silicone-breast-pump-with-suction-base',
    cta_label_zh: '查看产品',
    cta_label_en: 'View product',
    tagline_zh: '温和集奶，适合 Epsom salt 浸泡或日常温和收集',
    tagline_en: 'Gentle milk collection — useful for Epsom salt soaking or low-pressure expression',
    discounts: [
      { code: 'HK10YM', desc_zh: '专属折扣码', desc_en: 'Exclusive discount code' },
    ],
    safety_note_zh: '请使用温和吸力。如果乳头有疼痛、破皮、出血、严重发炎或受伤，请不要持续强吸。',
    safety_note_en: 'Use gentle suction only. Do NOT use strong continuous suction on a painful, cracked, bleeding, severely inflamed, or injured nipple.',
    eligibility: (submission) => {
      const { allIds, risk } = ctx(submission);
      // Show for nipple bleb / white spot (Epsom salt method)
      if (!allIds.includes('white_spot')) return false;
      // Never show during true red flags
      if (risk === 'red') return false;
      return true;
    },
  },
];

// Return the list of eligible products for a submission, in display order.
export const getEligibleProducts = (submission) => {
  if (!submission) return [];
  return PRODUCTS.filter((p) => {
    try { return p.eligibility(submission); } catch { return false; }
  });
};
